import { useState, useEffect, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthChange } from './services/auth';
import { setupPresence } from './services/presenceService';
import { getCurrentTheme, applyTheme, subscribeToSettings } from './services/settingsService';
import Login from './components/Login';
import UserProfile from './components/UserProfile';
import CreateRoom from './components/CreateRoom';
import JoinRoom from './components/JoinRoom';
import RoomLobby from './components/RoomLobby';
import Settings from './components/Settings';
import NotificationCenter from './components/NotificationCenter';
import MessagingCenter from './components/MessagingCenter';
import './styles/App.css';

// Error Boundary to catch and display React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', backgroundColor: '#ffebee', margin: '20px', borderRadius: '8px' }}>
          <h2 style={{ color: '#c62828' }}>Something went wrong</h2>
          <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
            <summary>Error Details (click to expand)</summary>
            <p style={{ color: '#d32f2f' }}>{this.state.error && this.state.error.toString()}</p>
            <p style={{ fontSize: '12px', color: '#666' }}>
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </p>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: '10px', padding: '10px 20px', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Apply saved theme on app load
    const savedTheme = getCurrentTheme();
    applyTheme(savedTheme);

    // Subscribe to authentication state changes
    const unsubscribe = onAuthChange((currentUser) => {
      setUser(currentUser);
      setLoading(false);

      // Subscribe to user settings when logged in
      if (currentUser) {
        // Set up presence tracking
        setupPresence(currentUser.uid, {
          displayName: currentUser.displayName || 'Anonymous',
          photoURL: currentUser.photoURL || null
        }).catch(err => {
          console.error('Error setting up presence in App:', err);
        });

        const settingsUnsubscribe = subscribeToSettings(currentUser.uid, (settings) => {
          // Apply theme when settings change
          if (settings.theme) {
            applyTheme(settings.theme);
          }
        });

        // Clean up settings subscription on logout
        return () => settingsUnsubscribe();
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <h1 className="loading-title">RageBaitStudio</h1>
          <p className="loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  // Protected Route wrapper component
  const ProtectedRoute = ({ children }) => {
    return user ? children : <Navigate to="/" replace />;
  };

  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          {/* Notification Center - Shows when user is logged in */}
          {user && <NotificationCenter userId={user.uid} currentUser={user} />}

          {/* Messaging Center - Shows when user is logged in */}
          {user && <MessagingCenter currentUser={user} />}

          <Routes>
          {/* Public route */}
          <Route
            path="/"
            element={user ? <Navigate to="/profile" replace /> : <Login />}
          />

          {/* Protected routes */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/create-room"
            element={
              <ProtectedRoute>
                <CreateRoom user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/join-room"
            element={
              <ProtectedRoute>
                <JoinRoom user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/room/:roomCode"
            element={
              <ProtectedRoute>
                <RoomLobby user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Catch-all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
